import { useQuery } from '@tanstack/react-query';

import { useRequest, QueryFunction, queryClient } from '@frontend/api';

type QuoteItemId = number;
type FileUuid = string;

type TableExtraction = {
  uuid: string;
  filename: string;
  page: number;
};

export type QuoteItemMatchesData = {
  bomTablesFoundIn: Record<QuoteItemId, TableExtraction[]>;
  fileAssignments: Record<QuoteItemId, FileUuid[]>;
};

export type UseQuoteItemMatchesParams = {
  quoteId: number | string | null;
};

export const QUOTE_ITEM_MATCHES_BASE_QUERY_KEY = [
  'quoteFileToQuoteItemMatches',
] as const;

export const getUseGetQuoteItemMatchesKey = (
  params: UseQuoteItemMatchesParams,
) => [...QUOTE_ITEM_MATCHES_BASE_QUERY_KEY, params.quoteId] as const;

/**
 * Invalidates quote item matches for every quote.
 *
 * Use when something changed that could affect the matches but the quote it belongs
 * to is not known at the call site - e.g. dismissing an extracted table from the
 * viewer, which can change the BOM tables found for a quote item.
 */
export const invalidateAllQuoteItemMatchesQueries = () => {
  queryClient.invalidateQueries({
    queryKey: QUOTE_ITEM_MATCHES_BASE_QUERY_KEY,
  });
};

export const useGetQuoteItemSuggestions: QueryFunction<
  QuoteItemMatchesData,
  [UseQuoteItemMatchesParams],
  ReturnType<typeof getUseGetQuoteItemMatchesKey>
> = (params, { requestOptions, queryOptions = {} } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getUseGetQuoteItemMatchesKey(params);
  const { quoteId } = params;

  if (quoteId == null) {
    queryOptions.enabled = false;
  }

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request(`/quotes/${params.quoteId}/line_item_suggestions`, {
        init: {
          signal,
        },
      });
    },
  });
};
